import {
  FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useBooleanValue, useHistoryStateControl } from '@edikk-edikk/hooks';
import loadable from '@loadable/component';
import { Subject } from 'rxjs/internal/Subject';
import { useUserContext } from '@edikk-edikk/shared';
import { ContentLoader } from '@edikk-edikk/country-common';
import { MenuContext, MenuContextType } from '#baseMenu/contexts';
import {
  Backdrop,
  Controls,
  Join,
  ContentWrapper,
} from '#baseMenu/components';
import { Variant } from '#baseMenu/objects';
import { SupportModalToggleContext, SupportModalToggleContextType } from '#baseSupportModal/contexts';
import menuCss from '../assets/menu.module.scss';
import { SupportModalHolder } from '#baseSupportModal/containers';
import { useMenuContentVisibleControl } from '#baseMenu/hooks';

const SupportModal = loadable(() => import('#baseSupportModal/containers/SupportModal'), {
  fallback: <ContentLoader />,
  resolveComponent: ({ SupportModal: Component }) => Component,
});

const Content = loadable(() => import('#baseMenu/components/content/Content'), {
  fallback: <ContentLoader />,
  resolveComponent: ({ Content: Component }) => Component,
});

const Menu: FC = () => {
  const { isAuth } = useUserContext();
  const [isVisibleContent, showContent, hideContent] = useBooleanValue();
  const [isVisibleBackdrop, showBackdrop, hideBackdrop] = useBooleanValue();
  const refOpenSubject = useRef<Subject<void>>(new Subject<void>());
  const refHideSubject = useRef<Subject<void>>(new Subject<void>());
  const defaultVariant = isAuth ? Variant.loggedIn : Variant.signIn;
  const [variant, setVariant] = useState<Variant>(defaultVariant);
  const { isOpen, open, hide } = useMenuContentVisibleControl(Content, refOpenSubject.current, refHideSubject.current);

  const { setId: setIdToHistoryState, removeId: removeIdFromHistoryState } = useHistoryStateControl({
    name: 'menu',
    id: 'menu',
    isVisible: isOpen,
    hide,
    open,
  });

  useEffect(() => {
    if (isOpen) {
      showBackdrop();
    } else {
      hideContent();
    }
  }, [hideContent, isOpen, showBackdrop]);

  const menuContextValue: MenuContextType = {
    Content,
    open,
    hide,
    isOpen,
    isVisibleContent,
    showContent,
    isVisibleBackdrop,
    hideBackdrop,
    setIdToHistoryState,
    removeIdFromHistoryState,
    refOpenSubject,
    refHideSubject,
    variant,
    setVariant,
  };

  const supportModalToggleContextValue: SupportModalToggleContextType = {
    SupportModal,
  };

  return (
    <SupportModalToggleContext value={supportModalToggleContextValue}>
      <MenuContext value={menuContextValue}>
        <div className={menuCss.menu}>
          <ContentWrapper />
          <Join />
          <Controls />
        </div>
        <Backdrop />
        <SupportModalHolder />
      </MenuContext>
    </SupportModalToggleContext>
  );
};

export { Menu };
